import { Component, ComponentRef, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { KVK_NETHERLANDS_PATTERN, SIREN_FRANCE_PATTERN } from '~shared/validators/pattern-validators';
import { ActivatedRoute, Router, UrlSerializer } from '@angular/router';
import { VAT_ID_CHECK_CONFIG_ITALY } from '~app/modules/authorization/constants/vat-id-check.constant';
import {
  APP_USER_BRANCHES_SELECTION_ROUTE,
  APP_USER_IDAM_ADD_BUSINESS_PROFILE_ROUTE,
  NUMBER_0,
  NUMBER_1,
} from '~shared/constants';
import { CompanyDetailsFromVatService } from '~app/modules/authorization/services/company-details-from-vat.service';
import {
  IEntrepreneurInfoFrance,
  IEntrepreneurInfoItaly,
  IEntrepreneurInfoNetherlands,
} from '~app/modules/account/modules/account-management/models/user-info.model';
import { TranslateService } from '@ngx-translate/core';
import { MmAlertComponent, MmAlertService } from '@metromarkets/components';
import { IdamRegisterTrackingService } from '~app/modules/authorization/services/idam-register-tracking.service';
import { ScrollUtils } from '~shared/utils/scroll.utils';
import { ErrorsUtil } from '~shared/utils/errors.util';
import { CSSHttpError } from '~shared/modules/error-handler/models/css-error.model';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { AuthorizationHttpService } from '~app/modules/authorization/services/authorization-http.service';
import { VatNumberAnonymizerService } from './vat-number-anonymizer.service';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
import { COUNTRY_BASED_VAT_ID_PREFIX, CountryCode } from '~shared/constants/country-code.enum';
import { CompanyDetailsFromIdService } from '../../services/company-details-from-id.service';
// prettier-ignore
import {
  ConfigureCountryWiseFieldsService
} from '~app/modules/authorization/services/configure-country-wise-fields.service';
// prettier-ignore
import {
  TierTwoRegistrationPromptService
} from '~app/modules/authorization/services/tier.two.registration.prompt.service';
import { ALERT_CONFIG } from '~app/modules/authorization/constants/alert.config.constant';
import { IBranchEntries, IBranchModel } from '../../models/branch.model';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-vat-kvk-id-check-for-tier-two-signup',
  templateUrl: './vat-kvk-id-check-for-tier-two-signup.component.html',
  styleUrls: ['./vat-kvk-id-check-for-tier-two-signup.component.scss'],
})
export class VatKvkIdCheckForTierTwoSignupComponent implements OnInit, OnDestroy {
  @ViewChild('form', { read: ElementRef }) formElement: ElementRef;

  public formGroup: FormGroup;
  public isSubmitting = false;
  currentCountry: CountryCode;

  public config = VAT_ID_CHECK_CONFIG_ITALY;
  vatPrefix = COUNTRY_BASED_VAT_ID_PREFIX[CountryCode.Italy];
  isRedirectFromKVK = false;
  branchData: IBranchEntries[];
  destroySubject = new Subject();

  private alertRefs: ComponentRef<MmAlertComponent>[] = [];

  // prettier-ignore
  constructor( // NOSONAR
    private authorizationHttpService: AuthorizationHttpService,
    private router: Router,
    private serializer: UrlSerializer,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private companyDetailsFromVatService: CompanyDetailsFromVatService,
    private translateService: TranslateService,
    protected idamRegisterTrackingService: IdamRegisterTrackingService,
    protected activeFeaturesConfigcatService: ActiveFeaturesConfigcatService,
    private alertService: MmAlertService,
    private serverLoggerService: ServerLoggerService,
    private companyDetailsFromIdService: CompanyDetailsFromIdService,
    private configureCountryWiseFieldsService: ConfigureCountryWiseFieldsService,
    private tierTwoRegistrationPromptService: TierTwoRegistrationPromptService,
  ) {
    this.currentCountry = MarketDetectionUtil.getCountryCode();
    this.companyDetailsFromIdService.clearSelectedBranchDataForTierTwo();
  }

  public ngOnInit(): void {
    const { formConfig, vatPrefix, isRedirectFromKVK } =
      this.configureCountryWiseFieldsService.createVATIdCheckConfig();

    this.config = formConfig;
    this.vatPrefix = vatPrefix;
    this.isRedirectFromKVK = isRedirectFromKVK;

    this.initializeForm();
  }

  public onSubmit(): void {
    const vatTaxNumber = this.formGroup.get(this.config?.name).value;

    if (this.alertRefs?.length) {
      this.alertRefs.forEach((alert) => this.alertService.removeAlert(alert));
    }

    if (this.formGroup.valid) {
      if (MarketDetectionUtil.isDutchMarket() || MarketDetectionUtil.isFrenchMarket()) {
        this.handleTaxNumberSubmit(vatTaxNumber);
      } else {
        const vatNumberWithPrefix = vatTaxNumber.includes(this.currentCountry.toUpperCase())
          ? vatTaxNumber
          : `${this.vatPrefix}${vatTaxNumber}`;

        this.handleVatIdSubmit(vatNumberWithPrefix);
      }
    } else {
      ScrollUtils.scrollToInvalidElement(this.formElement, this.formGroup);
      this.handleValidationEvent(vatTaxNumber);
    }
  }

  redirectToTierTwoSignupOnSuccess() {
    const { queryParams } = this.router.parseUrl(this.router.url);
    const { backUrl } = queryParams;

    const urlTree = this.router.createUrlTree([APP_USER_IDAM_ADD_BUSINESS_PROFILE_ROUTE], {
      queryParams: { verifiedVAT: true, backUrl },
    });

    const url = this.serializer.serialize(urlTree);
    this.router.navigateByUrl(url);
  }

  redirectToTierTwoAfterIdValidation(taxNumber, selectedBranchData) {
    this.companyDetailsFromIdService
      .getSelectedBranchData(taxNumber, selectedBranchData?.metaData?.branchNumber)
      .pipe(takeUntil(this.destroySubject))
      .subscribe((branchDataForTierTwo) => {
        const isRsin = !selectedBranchData?.metaData?.branchNumber;
        this.publishBranchDataAndNavigateToTierTwo(branchDataForTierTwo, taxNumber, isRsin);
      });
  }

  publishBranchDataAndNavigateToTierTwo(selectedBranchData, taxNumber, isRsin) {
    const companyData = this.companyDetailsFromIdService.formatTierTwoDataForSignup(
      selectedBranchData,
      taxNumber,
      isRsin
    );
    this.companyDetailsFromIdService.postCompanyData(
      companyData as IEntrepreneurInfoNetherlands | IEntrepreneurInfoFrance
    );
    this.companyDetailsFromIdService.publishSelectedBranchDataForTierTwo(selectedBranchData);

    const { queryParams } = this.router.parseUrl(this.router.url);
    const { backUrl } = queryParams;

    this.router.navigate([APP_USER_IDAM_ADD_BUSINESS_PROFILE_ROUTE], {
      queryParams: { backUrl },
    });
  }

  redirectAfterIdValidationnOnSuccess(taxNumber) {
    const { queryParams } = this.router.parseUrl(this.router.url);
    const { backUrl } = queryParams;

    const urlTree = this.router.createUrlTree([APP_USER_BRANCHES_SELECTION_ROUTE], {
      queryParams: { verifiedTaxId: true, taxNumber, backUrl },
    });

    const url = this.serializer.serialize(urlTree);
    this.router.navigateByUrl(url);
  }

  handleValidationEventFromAPI(error: CSSHttpError) {
    const errors = ErrorsUtil.collectVATNumberErrors(error);
    const message = ErrorsUtil.concatErrorsToString(errors);
    this.idamRegisterTrackingService.idamSignUpErrorEventTracking(message);
  }

  handleValidationEvent(vatNumber?: string): void {
    const errors = ErrorsUtil.collectFormErrors(this.formGroup);
    const message = ErrorsUtil.concatErrorsToString(errors);

    VatNumberAnonymizerService.vatNumber = vatNumber;
    this.serverLoggerService.error(message, VatNumberAnonymizerService.shapeLogData());

    this.idamRegisterTrackingService.idamSignUpErrorEventTracking(message);
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
  }

  private initializeForm(): void {
    let field = null;

    switch (this.currentCountry) {
      case CountryCode.Netherlands: {
        field = { kvkNumber: new FormControl('', [Validators.required, Validators.pattern(KVK_NETHERLANDS_PATTERN)]) };
        break;
      }
      case CountryCode.France: {
        field = {
          sirenNumber: new FormControl('', [Validators.required, Validators.pattern(SIREN_FRANCE_PATTERN)]),
        };
        break;
      }
      default: {
        field = {
          vatNumber: new FormControl('', [
            Validators.required,
            Validators.pattern(this.configureCountryWiseFieldsService.getVATPatternFromCountry()),
          ]),
        };
        break;
      }
    }

    this.formGroup = this.formBuilder.group(field);
  }

  private handleInvalidVatNumber(error): void {
    const { vatNumberTitleAlert, vatNumberAlert } = this.tierTwoRegistrationPromptService.handleInvalidVatNumber(error);
    this.fetchTranslationAndShowAlert(vatNumberTitleAlert, vatNumberAlert);
  }

  private fetchTranslationAndShowAlert(title, message) {
    this.translateService.get([title, message]).subscribe((translations) => {
      this.addAlert(translations[title], translations[message]);
    });
  }

  private addAlert(title, message) {
    const alertRef = this.alertService.addAlert({ ...ALERT_CONFIG, title, message });
    this.alertRefs = [...this.alertRefs, alertRef];
  }

  private handleVatIdSubmit(vatNumberWithPrefix: string) {
    this.isSubmitting = true;

    this.authorizationHttpService.getCompanyDetails(vatNumberWithPrefix).subscribe(
      (response: IEntrepreneurInfoItaly) => {
        this.companyDetailsFromVatService.postCompanyData(response);
        this.redirectToTierTwoSignupOnSuccess();
      },
      (errorDetails) => {
        this.isSubmitting = false;

        if (errorDetails?.error) {
          const errorType = errorDetails?.error?.validationErrors[NUMBER_0]?.type;
          this.handleValidationEventFromAPI(errorType);

          if (errorType) {
            ScrollUtils.scrollTop();
            this.handleInvalidVatNumber(errorType);
          }
        }
      }
    );
  }

  private handleTaxNumberSubmit(taxNumber: string) {
    this.isSubmitting = true;

    this.companyDetailsFromIdService.getBranchesById(taxNumber).subscribe(
      (response: IBranchModel) => {
        this.branchData = response?.entries;
        this.companyDetailsFromIdService.postBranchesData(this.branchData);

        const isSingleBranch = this.branchData?.length === NUMBER_1;

        if (isSingleBranch) {
          // FOR SINGLE BRANCH & FOR BRANCH WITH RSIN: GO TO TIER 2 SCREEN DIRECTLY
          this.redirectToTierTwoAfterIdValidation(taxNumber, this.branchData[NUMBER_0]);
        } else {
          // FOR MULTIPLE BRANCHES: GO TO BRANCH SELECTION SCREEN
          this.redirectAfterIdValidationnOnSuccess(taxNumber);
        }
      },
      (error) => {
        this.tierTwoRegistrationPromptService.handleInvalidKVKNumber();
        this.isSubmitting = false;
        return null;
      }
    );
  }
}
